import React, { useState, useEffect, useMemo } from 'react';
import { resolveElementStyle, buildPanelStyle } from '../../utils/mobileStyleUtils';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

export default function MobileShopTab({
  vaultCoins: propVaultCoins,
  cards: propCards,
  selectedSourceCoin: propSelectedSourceCoin,
  initialSelectedCard: propInitialSelectedCard,
  onSelectSourceCoin,
  cart = [],
  onAddToCart,
  onUpdateCartQuantity,
  onClearCart,
  cartCount,
  setCartCount,
  user,
  tenant,
  mobileStyles = null
}) {
  const vaultCoins = propVaultCoins || propCards || [];
  const [internalSelectedCoin, setInternalSelectedCoin] = useState(propSelectedSourceCoin || propInitialSelectedCard || null);
  const selectedSourceCoin = propSelectedSourceCoin !== undefined ? propSelectedSourceCoin : internalSelectedCoin;

  const resolvedPanels = useMemo(() => resolveElementStyle(mobileStyles, 'end_user:shop', 'content_panels'), [mobileStyles]);
  const resolvedTypo = useMemo(() => resolveElementStyle(mobileStyles, 'end_user:shop', 'typography'), [mobileStyles]);
  const customPanelStyle = useMemo(() => buildPanelStyle(resolvedPanels.style), [resolvedPanels]);
  const typoConfig = resolvedTypo.style || {};
  const handleSelectCoin = (coin) => {
    setInternalSelectedCoin(coin);
    if (onSelectSourceCoin) onSelectSourceCoin(coin);
  };

  const [activeSection, setActiveSection] = useState('catalog'); // 'catalog' | 'orders'
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCardPickerOpen, setIsCardPickerOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [orderSuccessMessage, setOrderSuccessMessage] = useState(null);

  // Address inputs for checkout
  const [deliveryName, setDeliveryName] = useState(() => `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '');
  const [deliveryAddress, setDeliveryAddress] = useState(user?.billing_address || '123 Harbor View Road');
  const [deliveryCity, setDeliveryCity] = useState(user?.billing_city || 'Miami');
  const [deliveryZip, setDeliveryZip] = useState(user?.billing_zip || '33101');

  // Determine active target coin
  const activeCoin = selectedSourceCoin || (vaultCoins.length > 0 ? vaultCoins[0] : null);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/merchandise/products`, {
      headers: {
        'Authorization': `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.products) setProducts(data.products);
      })
      .catch(err => console.warn('Failed to load merchandise products:', err))
      .finally(() => setLoadingProducts(false));
  }, []);

  const fetchOrders = () => {
    setLoadingOrders(true);
    fetch(`${API_BASE}/api/v1/merchandise/orders`, {
      headers: {
        'Authorization': `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.orders) setOrders(data.orders);
      })
      .catch(err => console.warn('Failed to fetch orders:', err))
      .finally(() => setLoadingOrders(false));
  };

  useEffect(() => {
    if (activeSection === 'orders') {
      fetchOrders();
    }
  }, [activeSection]);

  const handleOptionChange = (productId, option) => {
    setSelectedOptions(prev => ({
      ...prev,
      [productId]: option
    }));
  };

  const handleAddProduct = (product) => {
    const chosenOption = selectedOptions[product.id] || (product.options ? product.options[0] : null);
    onAddToCart({
      product_id: product.id,
      name: product.name,
      price: product.price,
      selected_option: chosenOption,
      source_coin_id: activeCoin?.id,
      source_coin_public_code: activeCoin?.public_code,
      source_card_title: activeCoin?.card_data?.species || activeCoin?.card_name || 'Trophy Card'
    });
  };

  const cartTotal = cart.reduce((sum, it) => sum + (it.price * (it.quantity || 1)), 0);

  const handleExecuteCheckout = async () => {
    if (cart.length === 0) return;
    setCheckoutSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/v1/merchandise/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          source_coin_id: activeCoin?.id,
          source_coin_public_code: activeCoin?.public_code,
          items: cart,
          delivery_name: deliveryName,
          delivery_address: deliveryAddress,
          delivery_city: deliveryCity,
          delivery_postal_code: deliveryZip,
          delivery_country: 'United States',
          payment_method: 'apple_pay'
        })
      });

      if (!res.ok) throw new Error('Failed to create order');
      const data = await res.json();

      onClearCart();
      setIsCartOpen(false);
      setOrderSuccessMessage(`Order #${data.order.id} placed successfully! Production has begun.`);
      setActiveSection('orders');
      fetchOrders();
    } catch (err) {
      alert(`Checkout failed: ${err.message}`);
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      paddingBottom: '90px', // room for floating bottom dock
      color: typoConfig.body_color || '#f8fafc',
      fontFamily: typoConfig.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      {/* Top Toggle: Products vs My Orders */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 20px 12px 20px',
        gap: '8px'
      }}>
        <div style={{
          display: 'flex',
          background: 'rgba(255, 255, 255, 0.06)',
          padding: '3px',
          borderRadius: '12px',
          flex: 1
        }}>
          <button
            type="button"
            onClick={() => setActiveSection('catalog')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '9px',
              border: 'none',
              background: activeSection === 'catalog' ? '#00c6ff' : 'transparent',
              color: activeSection === 'catalog' ? '#0f172a' : '#cbd5e1',
              fontWeight: activeSection === 'catalog' ? 800 : 600,
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Custom Keepsakes
          </button>

          <button
            type="button"
            onClick={() => setActiveSection('orders')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '9px',
              border: 'none',
              background: activeSection === 'orders' ? '#00c6ff' : 'transparent',
              color: activeSection === 'orders' ? '#0f172a' : '#cbd5e1',
              fontWeight: activeSection === 'orders' ? 800 : 600,
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            My Orders
          </button>
        </div>

        {/* Floating Cart Button */}
        {cart.length > 0 && (
          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '12px',
              color: '#ffffff',
              padding: '9px 14px',
              fontSize: '12px',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)'
            }}
          >
            <span>Cart</span>
            <span style={{
              background: '#ffffff',
              color: '#059669',
              borderRadius: '9999px',
              padding: '1px 6px',
              fontSize: '10px',
              fontWeight: 900
            }}>
              {cart.reduce((n, it) => n + (it.quantity || 1), 0)}
            </span>
          </button>
        )}
      </div>

      {orderSuccessMessage && (
        <div style={{
          margin: '0 16px 12px 16px',
          padding: '12px 16px',
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.4)',
          borderRadius: '12px',
          color: '#34d399',
          fontSize: '12px',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{orderSuccessMessage}</span>
          <button
            type="button"
            onClick={() => setOrderSuccessMessage(null)}
            style={{ background: 'none', border: 'none', color: '#34d399', cursor: 'pointer', fontWeight: 800 }}
          >
            ✕
          </button>
        </div>
      )}

      {activeSection === 'catalog' ? (
        <>
          {/* Active Target Card Hero Banner */}
          {activeCoin ? (
            <div style={{
              margin: '0 16px 14px 16px',
              padding: '12px 14px',
              background: 'linear-gradient(135deg, rgba(0, 198, 255, 0.12) 0%, rgba(0, 114, 255, 0.08) 100%)',
              border: '1px solid rgba(0, 198, 255, 0.3)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                {activeCoin.preview_front_url && (
                  <img
                    src={activeCoin.preview_front_url}
                    alt="Card Preview"
                    style={{
                      width: '38px',
                      height: '52px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      background: '#0f172a',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#38bdf8', fontWeight: 800, letterSpacing: '0.04em' }}>
                    Ordering Merchandise For:
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activeCoin.card_data?.species || activeCoin.card_name || `Card #${activeCoin.coin_number}`}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                    Code: <code style={{ color: '#38bdf8' }}>{activeCoin.public_code}</code>
                  </div>
                </div>
              </div>

              {vaultCoins.length > 1 && (
                <button
                  type="button"
                  onClick={() => setIsCardPickerOpen(true)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#f8fafc',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Change Card
                </button>
              )}
            </div>
          ) : (
            <div style={{
              margin: '0 16px 14px 16px',
              padding: '12px 14px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              fontSize: '12px',
              color: '#fbbf24'
            }}>
              💡 No cards found in your vault. When you claim or have cards logged, merchandise will be minted with your custom artwork.
            </div>
          )}

          {/* Product Items List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '0 16px' }}>
            {loadingProducts ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>
                Loading custom merchandise...
              </div>
            ) : (
              products.map((p) => {
                const currentOpt = selectedOptions[p.id] || (p.options ? p.options[0] : null);
                return (
                  <div
                    key={p.id}
                    style={{
                      ...customPanelStyle,
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        {p.badge && (
                          <span style={{
                            background: 'rgba(0, 198, 255, 0.14)',
                            color: '#38bdf8',
                            fontSize: '9.5px',
                            fontWeight: 800,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em'
                          }}>
                            {p.badge}
                          </span>
                        )}
                        <h4 style={{ fontSize: '15px', fontWeight: 800, margin: '4px 0 2px 0', color: '#f8fafc' }}>
                          {p.name}
                        </h4>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{p.category}</div>
                      </div>

                      <div style={{ fontSize: '18px', fontWeight: 900, color: '#00c6ff' }}>
                        ${(p.price ?? p.base_price ?? 0).toFixed(2)}
                      </div>
                    </div>

                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, lineHeight: 1.45 }}>
                      {p.description}
                    </p>

                    {/* Options Pills (e.g. Gold / Silver / Bronze) */}
                    {p.options && p.options.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {p.options.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleOptionChange(p.id, opt)}
                            style={{
                              background: currentOpt === opt ? 'rgba(0, 198, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                              border: currentOpt === opt ? '1px solid #00c6ff' : '1px solid rgba(255, 255, 255, 0.1)',
                              color: currentOpt === opt ? '#38bdf8' : '#94a3b8',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: currentOpt === opt ? 700 : 500,
                              cursor: 'pointer'
                            }}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Add to Basket Action */}
                    <button
                      type="button"
                      onClick={() => handleAddProduct(p)}
                      style={{
                        background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 14px',
                        fontSize: '12.5px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 14px rgba(0, 198, 255, 0.25)'
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <span>Add to Basket (${p.price.toFixed(2)})</span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        /* My Orders / Live Production Tracking */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '0 16px' }}>
          {loadingOrders ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>
              Loading your orders...
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#f8fafc' }}>No Orders Yet</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Your merchandise orders and production tracking will appear here.
              </div>
            </div>
          ) : (
            orders.map((ord) => (
              <div
                key={ord.id}
                style={{
                  background: 'rgba(17, 24, 39, 0.85)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff' }}>
                      Order #{ord.id}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      {ord.package_name} • ${ord.total_price.toFixed(2)}
                    </div>
                  </div>

                  <span style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#34d399',
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: '3px 8px',
                    borderRadius: '8px'
                  }}>
                    {ord.step_label}
                  </span>
                </div>

                {/* 5-Step Visual Production Stepper */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '8px 0',
                  position: 'relative'
                }}>
                  {/* Progress Line */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '10px',
                    right: '10px',
                    height: '2px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: 1
                  }}>
                    <div style={{
                      width: `${(ord.current_step / (ord.total_steps - 1)) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #00c6ff, #10b981)',
                      transition: 'width 0.4s ease'
                    }} />
                  </div>

                  {ord.steps.map((st, sIdx) => {
                    const isDone = sIdx <= ord.current_step;
                    const isCurrent = sIdx === ord.current_step;
                    return (
                      <div
                        key={st.key}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          zIndex: 2,
                          gap: '4px'
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: isDone ? '#10b981' : '#1e293b',
                          border: isCurrent ? '2px solid #00c6ff' : '2px solid rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 800,
                          color: '#ffffff'
                        }}>
                          {isDone ? '✓' : sIdx + 1}
                        </div>
                        <span style={{
                          fontSize: '8.5px',
                          fontWeight: isCurrent ? 700 : 500,
                          color: isCurrent ? '#00c6ff' : (isDone ? '#e2e8f0' : '#64748b'),
                          textAlign: 'center',
                          maxWidth: '54px',
                          lineHeight: 1.1
                        }}>
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Items in order */}
                {ord.items && ord.items.length > 0 && (
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {ord.items.map((it, iIdx) => (
                      <div key={iIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#cbd5e1' }}>
                        <span>{it.quantity}x {it.name} {it.selected_option ? `(${it.selected_option})` : ''}</span>
                        <span style={{ color: '#00c6ff', fontWeight: 700 }}>${(it.price * it.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Slide-Up Cart / Checkout Drawer */}
      {isCartOpen && (
        <div
          onClick={() => setIsCartOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '460px',
              maxHeight: '85vh',
              background: '#111827',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderBottom: 'none',
              padding: '20px',
              overflowY: 'auto',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              boxShadow: '0 -16px 40px rgba(0, 0, 0, 0.7)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#f8fafc' }}>
                Your Keepsake Basket
              </div>
              <button
                type="button"
                onClick={() => setIsCartOpen(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: '#cbd5e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Cart Items List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {cart.map((it, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc' }}>
                      {it.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {it.selected_option && <span>Option: {it.selected_option} • </span>}
                      <span>For: {it.source_card_title}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#00c6ff', fontWeight: 800, marginTop: '2px' }}>
                      ${(it.price * (it.quantity || 1)).toFixed(2)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => onUpdateCartQuantity(idx, (it.quantity || 1) - 1)}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: 'none',
                        color: '#fff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      -
                    </button>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#fff' }}>{it.quantity || 1}</span>
                    <button
                      type="button"
                      onClick={() => onUpdateCartQuantity(idx, (it.quantity || 1) + 1)}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: 'none',
                        color: '#fff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Delivery Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#cbd5e1' }}>
                Delivery Address (Demonstration / Proxy)
              </div>
              <input
                type="text"
                placeholder="Recipient Name"
                value={deliveryName}
                onChange={e => setDeliveryName(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: '#fff',
                  fontSize: '12px'
                }}
              />
              <input
                type="text"
                placeholder="Street Address"
                value={deliveryAddress}
                onChange={e => setDeliveryAddress(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: '#fff',
                  fontSize: '12px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="City"
                  value={deliveryCity}
                  onChange={e => setDeliveryCity(e.target.value)}
                  style={{
                    flex: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                />
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={deliveryZip}
                  onChange={e => setDeliveryZip(e.target.value)}
                  style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                />
              </div>
            </div>

            {/* Total & Checkout Button */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              paddingTop: '8px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#94a3b8' }}>Total Amount</span>
                <span style={{ fontSize: '20px', fontWeight: 900, color: '#00c6ff' }}>${cartTotal.toFixed(2)}</span>
              </div>

              {/* Apple Pay / Instant Proxy Checkout */}
              <button
                type="button"
                disabled={checkoutSubmitting}
                onClick={handleExecuteCheckout}
                style={{
                  background: '#000000',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: checkoutSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.6)'
                }}
              >
                <span> Pay (Proxy Demo Checkout)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Switch Source Card Modal */}
      {isCardPickerOpen && (
        <div
          onClick={() => setIsCardPickerOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '380px',
              background: '#111827',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '18px',
              padding: '18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '15px', fontWeight: 800 }}>Choose Trophy Card</div>
              <button
                type="button"
                onClick={() => setIsCardPickerOpen(false)}
                style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: '14px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {vaultCoins.map((coin) => (
                <div
                  key={coin.id}
                  onClick={() => {
                    handleSelectCoin(coin);
                    setIsCardPickerOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px',
                    borderRadius: '10px',
                    background: activeCoin?.id === coin.id ? 'rgba(0, 198, 255, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                    border: activeCoin?.id === coin.id ? '1px solid #00c6ff' : '1px solid transparent',
                    cursor: 'pointer'
                  }}
                >
                  {coin.preview_front_url && (
                    <img
                      src={coin.preview_front_url}
                      alt=""
                      style={{ width: '32px', height: '44px', objectFit: 'contain', borderRadius: '4px' }}
                    />
                  )}
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>
                      {coin.card_data?.species || coin.card_name || `Card #${coin.coin_number}`}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>#{coin.public_code}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
